import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-red.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-yellow-cabin.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryLimo from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryExec from "@/assets/sprinter-executive-2.jpg?format=webp&quality=72&w=1400&imagetools";
import gallerySuv from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/atlanta-airport-car-service";
const PAGE_TITLE = "Atlanta Airport Car Service | ATL Hartsfield-Jackson Luxury Transfers";
const PAGE_DESC =
  "Premium Atlanta airport car service to and from Hartsfield-Jackson (ATL), PDK, FTY, RYY & LZU. Mercedes-Benz Sprinter Limousines and Suburban SUVs with professional chauffeurs. Call (678) 431-7222.";

const faqs = [
  {
    q: "How much does a car service from Hartsfield-Jackson (ATL) cost?",
    a: "Flat-rate Atlanta airport car service from ATL is quoted as a flat rate for a Suburban SUV or a Mercedes-Benz Sprinter Limousine, with custom quotes given upon request based on destination (Buckhead, Midtown, Sandy Springs, Alpharetta, etc.) and time of day. All quotes include the chauffeur, meet-and-greet, and luggage handling.",
  },
  {
    q: "Do you meet me inside at baggage claim or at the curb?",
    a: "Both options are available. Standard service is curbside pickup at the assigned door. For VIP, family, and group arrivals we offer meet-and-greet inside baggage claim with a name card and luggage assistance for a small additional fee.",
  },
  {
    q: "What airports do you serve in Atlanta?",
    a: "Every Atlanta-area airport: Hartsfield-Jackson Atlanta International (ATL), DeKalb-Peachtree (PDK), Fulton County / Charlie Brown Field (FTY), Cobb County International (RYY), and Gwinnett County / Briscoe Field (LZU). Both commercial terminals and private/FBO sides are covered.",
  },
  {
    q: "Do you track my flight?",
    a: "Yes. Every airport transfer is monitored in real time. If your flight is early, your chauffeur is early. If your flight is delayed, your chauffeur waits, at no charge. We adjust automatically; you don't have to call us.",
  },
  {
    q: "Can a group fit in one Sprinter from the airport?",
    a: "Yes. Our Mercedes-Benz Sprinter Limousines seat up to 14 passengers with full luggage in the rear cargo area, the smartest way to move a wedding party, corporate group, family reunion, or sports team in one vehicle instead of three SUVs.",
  },
  {
    q: "How far in advance should I book Atlanta airport car service?",
    a: "For regular trips, 24–48 hours is ideal. For peak periods (Masters week, Super Bowl, Final Four, major conventions, Thanksgiving, Christmas, New Year's Eve), book 2–4 weeks ahead, fleet sells out.",
  },
  {
    q: "Which door do I meet my chauffeur at when I land at ATL?",
    a: "Domestic arrivals: your chauffeur texts you a door number on the Domestic Terminal North or South baggage-claim curb once you're on the ground. International arrivals: pickup is on the Concourse F / International Terminal upper or lower curb after customs. If you prefer, we hold a name card inside baggage claim near the carousel and walk you out.",
  },
  {
    q: "How long does it actually take to get out of Hartsfield-Jackson?",
    a: "Plan on 20–30 minutes from wheels-down to being in the vehicle for domestic arrivals with checked bags, and 45–75 minutes for international arrivals clearing customs. Carry-on only, domestic, and you can be rolling in about 15 minutes. We build this into every pickup so the vehicle is staged, not circling.",
  },
  {
    q: "How much luggage fits in a Sprinter for an airport run?",
    a: "The Sprinter Limousine's rear cargo area handles roughly 10–14 large checked bags plus carry-ons for a full group; the Executive Sprinter configuration takes about 8–10. Golf bags, ski bags, instrument cases, and film gear all fit, tell us the load when you book and we assign the right configuration.",
  },
  {
    q: "What time should you pick me up for a departing flight from ATL?",
    a: "Our standard for domestic departures is curb drop-off 2 hours before scheduled departure, and 3 hours for international. From Buckhead that means leaving roughly 2h45m before a domestic flight, longer during the 6–9am and 3–7pm traffic windows on I-75/85 through the Downtown Connector.",
  },
  {
    q: "Do you handle ATL to private terminal (Signature) connections?",
    a: "Yes. We regularly move passengers between the commercial terminals and Signature Flight Support on the south side of Hartsfield-Jackson, as well as between ATL and PDK, FTY, RYY, or LZU for onward private departures.",
  },
];

export const Route = createFileRoute("/atlanta-airport-car-service")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "atlanta airport car service, car service to atlanta airport, ATL airport car service, hartsfield-jackson car service, atlanta airport limo, ATL limo service, private airport transfer atlanta, luxury car service atlanta airport",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Atlanta Airport Car Service",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Airport Car Service",
            areaServed: [
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
              { type: "Airport", name: "Fulton County Airport (FTY)" },
              { type: "Airport", name: "Cobb County International Airport (RYY)" },
              { type: "Airport", name: "Gwinnett County Airport (LZU)" },
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Midtown Atlanta" },
            ],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • Airport Car Service"
      h1="Atlanta Airport Car Service"
      intro="You just landed. Let my family handle the drive. We serve ATL, PDK, FTY, RYY and LZU, 24/7, with Suburban SUVs, Mercedes-Benz Sprinter Limousines, flight tracking and meet-and-greet options."
      heroImage={heroImage}
      heroAlt="Executive Mercedes-Benz Sprinter interior with captain's chairs for Atlanta airport car service"
      midImage={midImage}
      midAlt="Interior of luxury Sprinter for Atlanta airport car service, executive seating and ambient lighting"
      sectionOneTitle="Your Flight, Your Bags, Your Ride Home"
      sectionOneBody={[
        "Choose a Suburban for solo travel, couples or a small family. For a larger party, I'll help match your passengers and luggage to the right Mercedes-Benz Sprinter Limousine.",
        "We track every flight and adjust for early arrivals or delays. My chauffeurs handle transfers for executives, families, wedding parties, touring artists, sports teams and private flyers.",
        "Atlanta Luxury Sprinters is the luxury Sprinter fleet of Empress Elite Limousine, my family's company. We connect every Atlanta-area airport with hotels, venues, homes and offices.",
      ]}
      bulletsTitle="Why Atlanta Books Our Airport Car Service"
      bulletsIntro="A professional chauffeur, a prepared vehicle and a clear pickup plan."
      bullets={[
        "Live flight tracking on every transfer, no surge fees on delays",
        "Curbside or inside-terminal meet-and-greet at ATL baggage claim",
        "Suburban SUVs for up to 6 or 7; Sprinter Limousines for up to 14",
        "Professional, full-time chauffeurs",
        "Flat-rate airport pricing, no surprises at drop-off",
        "Complimentary bottled water and Wi-Fi on most trips",
        "24/7 support with a real person, including red-eye arrivals",
      ]}
      coverageTitle="Airport Coverage & Drop-Off Areas"
      coverageIntro="Door-to-door transfers from every Atlanta-area airport to every neighborhood, hotel, and venue."
      coverage={[
        "Hartsfield-Jackson (ATL)",
        "DeKalb-Peachtree (PDK)",
        "Fulton County (FTY)",
        "Cobb County (RYY)",
        "Gwinnett County (LZU)",
        "Buckhead",
        "Midtown Atlanta",
        "Downtown Atlanta",
        "Sandy Springs",
        "Alpharetta & Dunwoody",
        "Vinings, Smyrna & Marietta",
        "Decatur, Brookhaven & Roswell",
      ]}
      routeTable={{
        title: "ATL Airport Drive Times to Metro Atlanta",
        intro:
          "Typical chauffeured drive times from the Hartsfield-Jackson curb. Add 15–30 minutes during the weekday 6:30–9:30am and 3:30–7:00pm windows on the Downtown Connector.",
        rows: [
          { route: "ATL → Downtown Atlanta", distance: "~10 mi", time: "20–30 min", notes: "I-85 N to the Downtown Connector; heavy inbound in the morning peak." },
          { route: "ATL → Midtown", distance: "~13 mi", time: "25–40 min", notes: "Connector to 10th/14th St; event traffic around the arts district." },
          { route: "ATL → Buckhead", distance: "~17 mi", time: "30–45 min", notes: "GA-400 split at the top of the Connector; slowest 4–7pm." },
          { route: "ATL → Sandy Springs", distance: "~24 mi", time: "35–55 min", notes: "GA-400 N; Perimeter congestion around Hammond Dr." },
          { route: "ATL → Alpharetta", distance: "~33 mi", time: "45–70 min", notes: "GA-400 N past Northridge; predictable off-peak." },
          { route: "ATL → Marietta / Vinings", distance: "~22 mi", time: "30–50 min", notes: "I-285 W is often faster than the Connector midday." },
          { route: "ATL → Decatur", distance: "~17 mi", time: "25–40 min", notes: "I-285 E to Memorial Dr avoids downtown entirely." },
          { route: "ATL → Athens", distance: "~85 mi", time: "1h 35m–2h", notes: "I-85 N to GA-316; long-distance flat rate available." },
        ],
        footnote:
          "Times reflect real chauffeured runs, not best-case mapping estimates. Your chauffeur is timed to your actual flight, not the schedule.",
      }}
      detailSections={[
        {
          title: "Terminal-by-Terminal Pickup at Hartsfield-Jackson",
          body: [
            "ATL is two terminals and seven concourses, and the pickup point depends on where you land. Your chauffeur waits in the cell lot until you confirm you're at baggage claim, then pulls to a specific numbered door so you never stand in the open.",
          ],
          list: [
            "Domestic Terminal North (Delta, most domestic): baggage claim doors N1–N6, upper curb for carry-on only departures.",
            "Domestic Terminal South (Southwest, American, United, JetBlue): baggage claim doors S1–S6.",
            "International Terminal (Concourse F): arrivals curb after customs, allow 45–75 minutes from landing.",
            "Plane Train / inter-terminal: if you land at Concourse E or F on a domestic connection, ride to Baggage Claim and exit on the domestic side.",
            "Meet-and-greet upgrade: chauffeur meets you inside at the carousel with a name card and handles your bags to the vehicle.",
          ],
        },
        {
          title: "Departures: How We Build Your Pickup Time",
          body: [
            "Late to ATL is not recoverable, security lines at the Main and North checkpoints regularly run 25–45 minutes, and longer during spring break, Thanksgiving, and convention weeks.",
          ],
          list: [
            "Domestic, checked bags: curb 2 hours before departure.",
            "International: curb 3 hours before departure.",
            "CLEAR / PreCheck, carry-on only: curb 90 minutes before departure.",
            "Weekday 6–9am or 3–7pm pickups: we add 20–30 minutes of buffer automatically.",
            "Group departures of 8+: we stage the Sprinter 15 minutes early for luggage loading.",
          ],
        },
        {
          title: "Choosing the Right Vehicle for Your Airport Run",
          list: [
            "Suburban SUV, up to 6 passengers (captain seats) or 7 (bench seats) with 4–5 checked bags. Executive solo travel, couples, small families.",
            "Executive Sprinter, 8–11 passengers, forward-facing captain's chairs, work tables. Corporate teams and crews.",
            "Sprinter Limousine, 12–14 passengers, wraparound lounge seating, starlight headliner. Wedding parties, group arrivals, celebrations.",
            "Multi-vehicle, for arrivals over 14 passengers we run staged Sprinters with a single point of contact so the group stays together.",
          ],
        },
      ]}
      gallery={{
        title: "The Fleet Meeting You at the Curb",
        intro:
          "Every vehicle in our airport rotation is detailed before every trip, these are the actual configurations you'll be riding in.",
        images: [
          { src: galleryExec, alt: "Executive Mercedes-Benz Sprinter with captain's chairs for Atlanta airport transfers", caption: "Executive Sprinter, 8–11 passengers, corporate airport runs" },
          { src: galleryLimo, alt: "Mercedes-Benz Sprinter Limousine interior with lounge seating for ATL group arrivals", caption: "Sprinter Limousine, 12–14 passengers with full luggage" },
          { src: gallerySuv, alt: "Black Chevrolet Suburban SUV for executive Atlanta airport car service", caption: "Suburban SUV, solo and small-party transfers" },
        ],
      }}
      faqTitle="Atlanta Airport Car Service FAQs"
      relatedTitle="Related Atlanta Transportation Services"
      faqs={faqs}
      related={[
        {
          to: "/thanksgiving-transportation-atlanta",
          title: "Thanksgiving Transportation",
          desc: "Family airport pickups and Thanksgiving rides in one Sprinter.",
        },
        {
          to: "/chauffeur-service-buckhead-from-airport",
          title: "Buckhead Airport Chauffeur",
          desc: "Direct chauffeured transfers into Buckhead from every Atlanta airport.",
        },
        {
          to: "/sprinter-limo-atlanta",
          title: "Sprinter Limo Atlanta",
          desc: "Mercedes-Benz Sprinter Limousines for groups, weddings, and VIP nights.",
        },
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service",
          desc: "FBO-side chauffeur service for private aviation arrivals and departures.",
        },
        {
          to: "/chauffeur-service-atlanta",
          title: "Chauffeur Service Atlanta",
          desc: "Professional private chauffeurs for airport pickups, executives and family visits.",
        },
      ]}
      ctaTitle="Text Me Your Flight Details"
      ctaBody="Text me your flight, destination, passenger count and luggage. I'll check availability myself and send your flat-rate quote in writing."
    />
  );
}
